import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Heart, Flame, HelpCircle, BookOpen } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const tabs = [
  { id: 'question', label: 'Ask Me Anything', icon: HelpCircle, color: '#FF00FF',
    placeholder: 'What\'s that one question about sex, intimacy, or dating you\'ve been too shy to ask anyone?',
    description: 'Your question might be answered in my next Instagram story or reel.' },
  { id: 'story', label: 'Share Your Story', icon: BookOpen, color: '#00FFFF',
    placeholder: 'That real-life experience around sex or intimacy that changed you — tell me about it...',
    description: 'Amazing real experiences deserve to be heard. I\'d love to read yours.' },
  { id: 'kink', label: 'Confess a Fantasy', icon: Flame, color: '#CCFF00',
    placeholder: 'That fantasy or kink you\'ve never told anyone about... this is your safe space to let it out.',
    description: 'No judgment. No shame. Just honesty between us.' },
];

const AskAcidRaniSection = () => {
  const [activeTab, setActiveTab] = useState('question');
  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [instagram, setInstagram] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const active = tabs.find(t => t.id === activeTab);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/submissions`, {
        submission_type: activeTab,
        content: content.trim(),
        name: name.trim() || 'Anonymous',
        instagram: instagram.trim(),
      });
      setSubmitted(true);
      setContent(''); setName(''); setInstagram('');
      setTimeout(() => setSubmitted(false), 4000);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('Submission error:', e);
    }
    setSending(false);
  };

  return (
    <section className="section-padding relative overflow-hidden" data-testid="ask-section">
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-10"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        >
          <motion.div className="inline-block mb-4"
            animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 4, repeat: Infinity }}
          >
            <MessageCircle className="w-10 h-10 mx-auto text-[#FF00FF]" />
          </motion.div>
          <h2 className="text-section font-heading text-white mb-3">
            Talk to <span className="text-gradient-pink">Acid Rani</span>
          </h2>
          <p className="text-[#FFD1DC] text-base max-w-xl mx-auto">
            Got a burning question about sex? A fantasy you{"'"}ve never shared?
            A real story that deserves to be told? This is your space. I read everything.
          </p>
        </motion.div>

        {/* Tab Selector */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => { setActiveTab(t.id); setSubmitted(false); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-heading font-bold uppercase tracking-wider transition-all ${
                  activeTab === t.id
                    ? 'text-[#0A0208] shadow-lg'
                    : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'
                }`}
                style={activeTab === t.id ? { background: t.color } : {}}
                data-testid={`ask-tab-${t.id}`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Submission Form */}
        <AnimatePresence mode="wait">
          {!submitted ? (
            <motion.div
              key={activeTab}
              className="p-6 md:p-8 rounded-2xl"
              style={{ background: `${active.color}04`, border: `1px solid ${active.color}20` }}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-white/40 text-xs mb-4 font-body">{active.description}</p>

              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={active.placeholder}
                rows={5}
                className="w-full p-4 rounded-xl text-sm leading-relaxed resize-none outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${active.color}15`,
                  color: '#FFD1DC',
                  fontFamily: "'Gloria Hallelujah', cursive",
                }}
                data-testid="ask-content-input"
              />

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div>
                  <label className="text-white/30 text-[10px] font-body block mb-1">Name (or stay anonymous)</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)}
                    placeholder="Anonymous" className="checkout-input text-xs" />
                </div>
                <div>
                  <label className="text-white/30 text-[10px] font-body block mb-1">Instagram (optional — to tag you)</label>
                  <input type="text" value={instagram} onChange={e => setInstagram(e.target.value)}
                    placeholder="@username" className="checkout-input text-xs" />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!content.trim() || sending}
                className={`mt-5 w-full py-3.5 rounded-full font-heading font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${
                  content.trim()
                    ? 'text-[#0A0208] hover:shadow-lg'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
                style={content.trim() ? { background: active.color } : {}}
                data-testid="ask-submit-btn"
              >
                <Send size={16} />
                {sending ? 'Sending...' : 'Send to Acid Rani'}
              </button>

              <p className="text-white/15 text-[10px] text-center mt-3">
                Everything is anonymous by default. Your submission may be featured on Instagram (with credit if you want).
              </p>
            </motion.div>
          ) : (
            <motion.div
              className="text-center p-10 rounded-2xl"
              style={{ background: `${active.color}06`, border: `1px solid ${active.color}20` }}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            >
              <Heart className="w-10 h-10 mx-auto mb-3" style={{ color: active.color }} />
              <h3 className="font-heading text-xl text-white mb-2">Received with love</h3>
              <p className="text-white/40 text-sm">
                {activeTab === 'question'
                  ? 'Your question might appear in my next story or reel. Stay tuned.'
                  : activeTab === 'story'
                  ? 'Thank you for sharing something so personal. I\'ll read it tonight.'
                  : 'That took courage. No judgment — only appreciation.'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AskAcidRaniSection;
